import {
  bg,
  de,
  enUS,
  es,
  fr,
  hu,
  it,
  ja,
  type Locale,
  pt,
  ro,
  sk,
  sr,
} from "date-fns/locale";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller } from "react-hook-form";
import { type Translationtype } from "utils";
import { Tooltip } from "../components/tooltip";

type DateRangeValue = {
  startDate: string | null;
  endDate: string | null;
};

interface IFormSingleDateInputProps {
  errors: any;
  control: any;
  name: string;
  title: string;
  className?: string;
  minDate?: any;
  maxDate?: any;
  tooltip?: string | null;
  /** App locale name (e.g. "en", "hu", "de"). Drives the date format. */
  i18n?: string;
  /** date-fns format override. When omitted, derived from the locale. */
  displayFormat?: string;
  disabledDates?: { startDate: unknown; endDate: unknown }[] | null;
  startWeekOn?: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  popoverDirection?: "up" | "down";
  /** Accepted for drop-in compatibility with FormDateInput; this picker is always single. */
  asSingle?: boolean;
  t: Translationtype;
}

const localeMap: Record<string, Locale> = {
  hu,
  de,
  en: enUS,
  ja,
  fr,
  sk,
  it,
  pt,
  ro,
  es,
  bg,
  sr,
};

const weekStartMap: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

function toValidDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const dateOnly = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/.exec(
      value.trim()
    );
    if (dateOnly?.groups) {
      const { year, month, day } = dateOnly.groups;
      const localDate = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(localDate.getTime()) ? null : localDate;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "object" && value !== null && "toDate" in value) {
    const maybeDate = (value as { toDate?: () => Date }).toDate?.();
    if (!maybeDate) return null;
    return Number.isNaN(maybeDate.getTime()) ? null : maybeDate;
  }
  return null;
}

function resolveSelected(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === "object" && value !== null && "startDate" in value) {
    return toValidDate((value as { startDate?: unknown }).startDate);
  }
  return toValidDate(value);
}

function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function FormSingleDateInput({
  errors,
  control,
  name,
  title,
  minDate,
  maxDate,
  className,
  disabledDates,
  tooltip = null,
  i18n = "hu",
  displayFormat,
  startWeekOn = "mon",
  placeholder,
  readOnly = false,
  disabled,
  isRequired = false,
  popoverDirection,
}: IFormSingleDateInputProps) {
  const currentLang = i18n.split("-")[0];
  const dateFnsLocale = localeMap[currentLang] || enUS;
  const localeDateFormat =
    displayFormat ||
    dateFnsLocale.formatLong?.date({ width: "short" }) ||
    "yyyy-MM-dd";
  const placeholderText =
    placeholder ||
    localeDateFormat.replace(/[a-z]/g, (char) => char.toUpperCase());
  const calendarStartDay = weekStartMap[startWeekOn] ?? 1;

  const excludeDateIntervals = (disabledDates || [])
    .map((range) => {
      const start = toValidDate(range?.startDate);
      const end = toValidDate(range?.endDate);
      return start && end ? { start, end } : null;
    })
    .filter(
      (interval): interval is { start: Date; end: Date } => interval !== null
    );

  return (
    <div className={`${className || ""} w-full`}>
      <label htmlFor={name} className="block mb-1 text-textColor">
        {title}
        {isRequired ? <span className="text-red-500"> *</span> : null}
        {tooltip ? <Tooltip text={tooltip} /> : null}
      </label>
      <Controller
        control={control}
        name={name}
        render={({ field: { value, ref: _ref, onChange, ...fieldProps } }) => {
          const selectedDate = resolveSelected(value);
          const minDateValue = toValidDate(minDate);
          const change = (date: Date | null): DateRangeValue =>
            date
              ? { startDate: toYmd(date), endDate: toYmd(date) }
              : { startDate: null, endDate: null };
          return (
            <div>
              <DatePicker
                {...fieldProps}
                id={name}
                name={name}
                disabled={disabled || false}
                readOnly={readOnly}
                selected={selectedDate}
                onChange={(date) => onChange(change(date))}
                locale={dateFnsLocale}
                dateFormat={localeDateFormat}
                placeholderText={placeholderText}
                minDate={minDateValue || undefined}
                maxDate={toValidDate(maxDate) || undefined}
                openToDate={
                  !selectedDate && minDateValue ? minDateValue : undefined
                }
                excludeDateIntervals={
                  excludeDateIntervals.length ? excludeDateIntervals : undefined
                }
                calendarStartDay={calendarStartDay}
                autoComplete="off"
                isClearable={!disabled && !readOnly}
                showPopperArrow={false}
                popperPlacement={
                  popoverDirection === "up" ? "top-start" : "bottom-start"
                }
                wrapperClassName="w-full"
                className="input input-bordered block h-[48px] w-full px-4 py-2 border !border-brownDarker rounded-md text-brown focus:outline-none focus:border-black focus:border-2"
              />
            </div>
          );
        }}
      />
      {!disabled && errors[name]?.message ? (
        <p className="text-sm my-3 text-error">
          {errors[name]?.message as any}
        </p>
      ) : null}
    </div>
  );
}
